import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getLibraryBackfill, startLibraryBackfill } from '../../../api/referenceMaterialApi';
import { AI_REFERENCE_CONSENT_EVENT } from '../../../utils/aiReferenceConsentCard';

export const BACKFILL_POLL_MS = 3000;

const finishedText = (view) => {
  if (view.status === 'stopped') {
    return `AI 참고를 꺼서 모으기를 멈췄어요 · ${view.saved}개까지 모았어요`;
  }
  if (view.saved > 0) return `지난 채팅 파일 ${view.saved}개를 참고 자료에 모았어요`;
  return view.duplicate > 0 ? '이미 참고 자료에 있는 파일이었어요' : '새로 모을 파일이 없었어요';
};

/**
 * 참고 자료 섹션의 '지난 채팅 파일 모으기' 카드.
 * 서버가 대상(남아 있는 대화의 문서 첨부, 학생 정보·생기부·상담 대화 제외)과 개수를 정하고,
 * 화면을 닫아도 서버가 이어서 모은다. 동의가 꺼져 있거나 모을 파일이 없으면 그리지 않는다.
 */
const LibraryBackfillCard = ({ currentUser, isPro, maxAuto, onCollected }) => {
  const [view, setView] = useState(null);
  const [finished, setFinished] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');
  const runningRef = useRef(false);
  const onCollectedRef = useRef(onCollected);
  onCollectedRef.current = onCollected;

  const load = useCallback(async () => {
    try {
      setView(await getLibraryBackfill(currentUser));
    } catch (_error) {
      setView(null);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
    window.addEventListener(AI_REFERENCE_CONSENT_EVENT, load);
    return () => window.removeEventListener(AI_REFERENCE_CONSENT_EVENT, load);
  }, [load]);

  useEffect(() => {
    if (view?.status === 'running') {
      runningRef.current = true;
      const timer = window.setTimeout(load, BACKFILL_POLL_MS);
      return () => window.clearTimeout(timer);
    }
    if (runningRef.current && (view?.status === 'done' || view?.status === 'stopped')) {
      runningRef.current = false;
      setFinished(view);
      onCollectedRef.current?.();
    }
    return undefined;
  }, [load, view]);

  const handleStart = async () => {
    if (isStarting) return;
    setIsStarting(true);
    setError('');
    setFinished(null);
    try {
      const next = await startLibraryBackfill(currentUser);
      if (next.status === 'running') runningRef.current = true;
      else if (next.status === 'done' || next.status === 'stopped') setFinished(next);
      setView(next);
    } catch (requestError) {
      setError(requestError?.message || '파일을 모으지 못했어요. 잠시 뒤 다시 시도해주세요.');
    } finally {
      setIsStarting(false);
    }
  };

  if (finished) {
    return (
      <div className="refmat-backfill refmat-backfill--done" role="status">
        <strong className="refmat-backfill__title">{finishedText(finished)}</strong>
        {finished.skipped_sensitive > 0 && (
          <span className="refmat-backfill__desc">
            학생 정보로 보이는 파일 {finished.skipped_sensitive}개는 뺐어요
          </span>
        )}
        {finished.saved > 0 && (
          <span className="refmat-backfill__desc">
            채팅에서 &apos;지난번에 올린 ○○ 자료로&apos;라고 하면 찾아 써요
          </span>
        )}
      </div>
    );
  }

  if (view?.status === 'running') {
    const percent = view.total > 0 ? Math.round((view.done / view.total) * 100) : 0;
    return (
      <div className="refmat-backfill" role="status">
        <strong className="refmat-backfill__title">
          지난 채팅 파일을 모으고 있어요 ({view.done}/{view.total})
        </strong>
        <div
          aria-label="모으기 진행"
          aria-valuemax={view.total}
          aria-valuemin={0}
          aria-valuenow={view.done}
          className="refmat-backfill__track"
          role="progressbar"
        >
          <span className="refmat-backfill__bar" style={{ width: `${percent}%` }} />
        </div>
        <span className="refmat-backfill__desc">이 화면을 닫아도 계속 모아요</span>
      </div>
    );
  }

  const count = view?.candidates || 0;
  if (!count || view?.status === 'unavailable') return null;

  let limitNote = '';
  if (!isPro) {
    limitNote = view.limited
      ? `직접 적은 텍스트와 따로, 채팅 파일은 최근 ${maxAuto}개까지 남아서 최근 ${count}개만 모아요`
      : `직접 적은 텍스트와 따로, 채팅 파일은 최근 ${maxAuto}개까지 남아요`;
  } else if (view.limited) {
    limitNote = `자료함 자리가 남은 만큼 최근 ${count}개만 모아요`;
  }

  return (
    <div className="refmat-backfill">
      <strong className="refmat-backfill__title">
        지난 채팅에 올린 파일 {count}개도 모을 수 있어요
      </strong>
      <span className="refmat-backfill__desc">
        학생 정보로 보이는 파일과 생기부·상담 대화 파일은 빼고 모아요
      </span>
      {limitNote && <span className="refmat-backfill__desc">{limitNote}</span>}
      {error && <span className="refmat-backfill__error" role="alert">{error}</span>}
      <button
        className="refmat-backfill__cta"
        disabled={isStarting}
        onClick={handleStart}
        type="button"
      >
        {isStarting ? '모으는 중…' : `파일 ${count}개 모으기`}
      </button>
    </div>
  );
};

export default LibraryBackfillCard;
